/**
 * Working documents (notes/checklists/ADRs/excalidraw du candidat) + dossiers.
 *
 * @remarks
 * Découpé du legacy. Le contenu Lexical / excalidraw vit dans `content`
 * (markdown ou JSON sérialisé). Le store reste agnostique du moteur d'édition.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { uid } from '../lib/status-helpers';
import type { WorkingDocTemplate, WorkingDocument, WorkingFolder } from '../types';

interface WorkingDocsState {
  workingDocuments: Record<string, WorkingDocument[]>;
  workingFolders: Record<string, WorkingFolder[]>;
  activeWorkingDocId: string | null;

  createWorkingDocument: (
    evaluationId: string,
    name: string,
    template: WorkingDocTemplate,
  ) => string;
  updateWorkingDocument: (
    id: string,
    updates: Partial<
      Pick<WorkingDocument, 'content' | 'name' | 'selectedForSubmission' | 'folderId'>
    >,
  ) => void;
  deleteWorkingDocument: (id: string) => void;
  setActiveWorkingDoc: (id: string | null) => void;

  createWorkingFolder: (evaluationId: string, name: string) => string;
  renameWorkingFolder: (id: string, name: string) => void;
  deleteWorkingFolder: (id: string) => void;
  moveDocumentToFolder: (docId: string, folderId: string | undefined) => void;
  moveFolderToFolder: (folderId: string, parentFolderId: string | undefined) => void;

  syncApiDocs: (key: string, docs: WorkingDocument[]) => void;
}

/** Helpers — `exactOptionalPropertyTypes: true` interdit `folderId: undefined`. */
function setDocFolder(doc: WorkingDocument, folderId: string | undefined): WorkingDocument {
  if (folderId === undefined) {
    const { folderId: _omit, ...rest } = doc;
    return rest;
  }
  return { ...doc, folderId };
}
function setFolderParent(folder: WorkingFolder, parentFolderId: string | undefined): WorkingFolder {
  if (parentFolderId === undefined) {
    const { parentFolderId: _omit, ...rest } = folder;
    return rest;
  }
  return { ...folder, parentFolderId };
}

function getTemplateContent(name: string, template: WorkingDocTemplate): string {
  switch (template) {
    case 'free':
      return '';
    case 'questions':
      return `# ${name}\n\n## Par document\n\n## En attente de réponse\n\n## Répondu\n\n`;
    case 'adr':
      return `# ${name}\n\n## Contexte\n\n## Décision\n\n## Conséquences\n\n`;
    case 'checklist':
      return `# ${name}\n\n- [ ] \n- [ ] \n- [ ] \n`;
    case 'readme':
      return `# ${name}\n\n## Setup\n\n\`\`\`bash\n# install\n\`\`\`\n\n## Usage\n\n## Architecture\n\n`;
    case 'excalidraw':
      return '';
  }
}

const SEED_WORKING_DOCS: Record<string, WorkingDocument[]> = {
  'evaluation-1-2': [
    {
      id: 'wdoc-e12-checklist',
      evaluationId: 'evaluation-1-2',
      name: 'Checklist composants',
      template: 'checklist',
      content:
        '# Checklist composants\n\n- [x] Header responsive\n- [x] Sidebar navigation\n- [x] Panel générique (tabs + contenu)\n- [ ] Vue Dashboard\n- [ ] Vue Chat\n- [ ] Tests unitaires atoms\n- [ ] Coverage ≥ 80%\n',
      createdAt: Date.now() - 2 * 60 * 60 * 1000,
      updatedAt: Date.now() - 15 * 60 * 1000,
      selectedForSubmission: true,
    },
    {
      id: 'wdoc-e12-questions',
      evaluationId: 'evaluation-1-2',
      name: 'Questions — Specs UI',
      template: 'questions',
      content:
        "# Questions — Specs UI\n\n## Par document\n\n- §2.1 : Les animations sont-elles obligatoires ?\n\n## En attente de réponse\n\n- Faut-il gérer le responsive mobile ou uniquement desktop ?\n\n## Répondu\n\n- Q: Quelle librairie d'icônes ? → Lucide React uniquement\n",
      createdAt: Date.now() - 3 * 60 * 60 * 1000,
      updatedAt: Date.now() - 45 * 60 * 1000,
      selectedForSubmission: false,
    },
    {
      id: 'wdoc-e12-notes',
      evaluationId: 'evaluation-1-2',
      name: 'Notes — Archi composants',
      template: 'free',
      content:
        '# Notes — Archi composants\n\nUtiliser Atomic Design : atoms → molecules → organisms.\n\nLe Panel doit être un container générique — ne pas coupler avec le contenu.\n\nZustand pour la persistance des tabs actifs → UX IDE.\n',
      createdAt: Date.now() - 4 * 60 * 60 * 1000,
      updatedAt: Date.now() - 2 * 60 * 60 * 1000,
      selectedForSubmission: false,
    },
  ],
};

export const useWorkingDocsStore = create<WorkingDocsState>()(
  devtools(
    persist(
      (set) => ({
        workingDocuments: SEED_WORKING_DOCS,
        workingFolders: {},
        activeWorkingDocId: null,

        createWorkingDocument: (evaluationId, name, template) => {
          const id = uid('wdoc');
          const now = Date.now();
          const doc: WorkingDocument = {
            id,
            evaluationId,
            name,
            template,
            content: getTemplateContent(name, template),
            createdAt: now,
            updatedAt: now,
            selectedForSubmission: false,
          };
          set((state) => ({
            workingDocuments: {
              ...state.workingDocuments,
              [evaluationId]: [doc, ...(state.workingDocuments[evaluationId] ?? [])],
            },
            activeWorkingDocId: id,
          }));
          return id;
        },

        updateWorkingDocument: (id, updates) => {
          set((state) => ({
            workingDocuments: Object.fromEntries(
              Object.entries(state.workingDocuments).map(([evaluationId, docs]) => [
                evaluationId,
                docs.map((doc) =>
                  doc.id === id ? { ...doc, ...updates, updatedAt: Date.now() } : doc,
                ),
              ]),
            ),
          }));
        },

        deleteWorkingDocument: (id) => {
          set((state) => ({
            workingDocuments: Object.fromEntries(
              Object.entries(state.workingDocuments).map(([evaluationId, docs]) => [
                evaluationId,
                docs.filter((doc) => doc.id !== id),
              ]),
            ),
            activeWorkingDocId: state.activeWorkingDocId === id ? null : state.activeWorkingDocId,
          }));
        },

        setActiveWorkingDoc: (id) => {
          set({ activeWorkingDocId: id });
        },

        createWorkingFolder: (evaluationId, name) => {
          const id = uid('wfolder');
          const folder: WorkingFolder = { id, evaluationId, name, createdAt: Date.now() };
          set((state) => ({
            workingFolders: {
              ...state.workingFolders,
              [evaluationId]: [folder, ...(state.workingFolders[evaluationId] ?? [])],
            },
          }));
          return id;
        },

        renameWorkingFolder: (id, name) => {
          set((state) => ({
            workingFolders: Object.fromEntries(
              Object.entries(state.workingFolders).map(([evaluationId, folders]) => [
                evaluationId,
                folders.map((f) => (f.id === id ? { ...f, name } : f)),
              ]),
            ),
          }));
        },

        deleteWorkingFolder: (id) => {
          set((state) => {
            let parentFolderId: string | undefined;
            for (const folders of Object.values(state.workingFolders)) {
              const folder = folders.find((f) => f.id === id);
              if (folder) {
                parentFolderId = folder.parentFolderId;
                break;
              }
            }
            return {
              workingFolders: Object.fromEntries(
                Object.entries(state.workingFolders).map(([evaluationId, folders]) => [
                  evaluationId,
                  folders
                    .filter((f) => f.id !== id)
                    .map((f) => (f.parentFolderId === id ? setFolderParent(f, parentFolderId) : f)),
                ]),
              ),
              workingDocuments: Object.fromEntries(
                Object.entries(state.workingDocuments).map(([evaluationId, docs]) => [
                  evaluationId,
                  docs.map((doc) =>
                    doc.folderId === id ? setDocFolder(doc, parentFolderId) : doc,
                  ),
                ]),
              ),
            };
          });
        },

        moveDocumentToFolder: (docId, folderId) => {
          set((state) => ({
            workingDocuments: Object.fromEntries(
              Object.entries(state.workingDocuments).map(([evaluationId, docs]) => [
                evaluationId,
                docs.map((doc) => (doc.id === docId ? setDocFolder(doc, folderId) : doc)),
              ]),
            ),
          }));
        },

        moveFolderToFolder: (folderId, parentFolderId) => {
          set((state) => ({
            workingFolders: Object.fromEntries(
              Object.entries(state.workingFolders).map(([evaluationId, folders]) => [
                evaluationId,
                folders.map((f) => (f.id === folderId ? setFolderParent(f, parentFolderId) : f)),
              ]),
            ),
          }));
        },

        syncApiDocs: (key, docs) => {
          set((state) => ({
            workingDocuments: { ...state.workingDocuments, [key]: docs },
          }));
        },
      }),
      {
        name: 'saas-front-v2:tech-tests-working-docs',
        version: 1,
        partialize: (state) => ({
          workingDocuments: state.workingDocuments,
          workingFolders: state.workingFolders,
        }),
        /**
         * Ce store est en `version: 1` depuis sa création : aucune version
         * antérieure n'existe. `migrate` n'est donc atteint que sur un payload
         * d'origine inconnue (downgrade, entrée éditée à la main), dont la
         * forme n'est pas garantie — on repart d'un espace **vide**.
         *
         * Le fallback ne doit surtout pas valoir `SEED_WORKING_DOCS` : le
         * candidat verrait trois documents qu'il n'a jamais écrits à la place
         * des siens, et zustand réécrit le résultat de la migration dans le
         * storage — les vrais seraient perdus définitivement.
         *
         * ⚠️ Au prochain bump, ajouter la transformation **avant** ce retour et
         * comparer avec `<` : `if (oldVersion < 2) { …adapter… }`. Ne jamais
         * tester `oldVersion === <version courante>` — zustand n'appelle
         * `migrate` que si les versions diffèrent, la branche serait morte.
         * Motif en cascade de référence : `use-layout-store`. (SAAS-903)
         */
        migrate: () => ({ workingDocuments: {}, workingFolders: {} }),
      },
    ),
    { name: 'working-docs', enabled: import.meta.env.DEV },
  ),
);
